import React, { FC } from "react";
import {
  ProportionBarLegend,
  ProportionBarLegendDot,
  ProportionBarLegendItem,
  ProportionBarSegment,
  ProportionBarTrack,
  ProportionBarWrap,
  V3ChartSeries,
} from "./ProportionBar.styled";

export type { V3ChartSeries };

export interface V3ProportionBarSegment {
  value: number;
  series: V3ChartSeries;
  label?: string;
}

export interface V3ProportionBarProps {
  segments: V3ProportionBarSegment[];
  height?: number;
  showLegend?: boolean;
  testID?: string;
}

const formatPercentage = (percentage: number): string =>
  `${Math.round(percentage * 10) / 10}%`;

export const V3ProportionBar: FC<V3ProportionBarProps> = ({
  segments,
  height = 10,
  showLegend,
  testID = "v3-proportion-bar",
}) => {
  const total = segments.reduce((sum, segment) => sum + segment.value, 0);
  const parts = segments.map((segment) => ({
    ...segment,
    percentage: total > 0 ? (segment.value / total) * 100 : 0,
  }));

  const description = parts
    .map((part, index) =>
      [part.label ?? `Segment ${index + 1}`, formatPercentage(part.percentage)]
        .join(" ")
    )
    .join(", ");

  return (
    <ProportionBarWrap>
      <ProportionBarTrack
        $height={height}
        role="img"
        aria-label={description}
        data-testid={testID}
      >
        {parts.map((part, index) => (
          <ProportionBarSegment
            key={`${part.series}-${index}`}
            $series={part.series}
            $percentage={part.percentage}
            data-testid={`${testID}-segment-${index}`}
          />
        ))}
      </ProportionBarTrack>
      {showLegend && (
        <ProportionBarLegend data-testid={`${testID}-legend`}>
          {parts.map((part, index) => (
            <ProportionBarLegendItem key={`${part.series}-${index}`}>
              <ProportionBarLegendDot $series={part.series} aria-hidden={true} />
              {part.label ?? `Segment ${index + 1}`}
              {" · "}
              {formatPercentage(part.percentage)}
            </ProportionBarLegendItem>
          ))}
        </ProportionBarLegend>
      )}
    </ProportionBarWrap>
  );
};
